import { CAPTURE, POSITION_SOURCE_LABEL, type PositionSource } from '@kilroy/shared';
import {
  formatAccuracy,
  formatCoord,
  metersToFeet,
  type GpsPhase,
  type GpsState,
  LOCK_THRESHOLD_M,
} from '../../media/gps';
import { IconFix } from '../icons';

/* One row per phase, and the type makes that mandatory.
 *
 * The previous version was `locked ? 'Locked' : 'Settling…'` — a binary over a
 * five-member union, so `denied` and `unavailable` both rendered as "Settling…"
 * and told someone to keep waiting for a fix that was never coming.
 *
 * A `Record<GpsPhase, …>` cannot be written that way. Add a member to GpsPhase
 * and this stops compiling until it is handled, which is the difference between
 * a bug we fixed and a bug we can no longer write. A test would have to be
 * remembered; the compiler does not forget.
 */
const PHASE: Record<
  GpsPhase,
  { pill: string; state: string; coords: string; hint: string | null }
> = {
  idle:        { pill: CAPTURE.gpsSettling, state: 'state--local',     coords: 'Acquiring…',   hint: null },
  settling:    { pill: CAPTURE.gpsSettling, state: 'state--local',     coords: 'Acquiring…',   hint: null },
  locked:      { pill: CAPTURE.gpsLocked,   state: 'state--synced',    coords: '',             hint: null },
  denied:      { pill: CAPTURE.gpsOff,      state: 'state--attention', coords: 'Location off', hint: CAPTURE.gpsDenied },
  unavailable: { pill: CAPTURE.gpsNoSignal, state: 'state--attention', coords: 'No position',  hint: CAPTURE.gpsUnavailable },
};

/* The GPS row.
 *
 * Two rules, both from the spec and both about honesty:
 *
 *   · Accuracy is ALWAYS recorded and always shown. The number is part of the
 *     fact, not a quality score to hide when it's bad.
 *   · A poor fix never blocks a save. Waiting for ±5m on a ridge with three
 *     satellites is how an observation goes unrecorded, and a missed
 *     observation is worth less than a rough one.
 *
 * "Locked" at ≤10m is the training-grade threshold from the KPI list, so the
 * lock state is telling you something real: this record will count toward the
 * Phase 4 dial.
 */

export function GpsRow({
  gps,
  manual,
  onType,
  onClearManual,
}: {
  gps: GpsState;
  manual?: { lat: number; lon: number; accuracy: number; source: string } | null;
  onType?: () => void;
  onClearManual?: () => void;
}) {
  const { fix, phase } = gps;
  const locked = phase === 'locked';
  const p = PHASE[phase];

  return (
    <div className="field">
      <div className="label">Position</div>

      {/* A typed position replaces the row entirely rather than sitting beside
          it. Two coordinate pairs on one screen is how the wrong one gets read
          aloud over a radio. */}
      {manual ? (
        <div className="gps gps--locked">
          <div className="gps__icon" aria-hidden="true">
            <IconFix size={26} />
          </div>
          <div className="grow">
            <div className="gps__coords mono">
              <span>{formatCoord(manual.lat, 'lat')}</span>
              <span>{formatCoord(manual.lon, 'lon')}</span>
            </div>
            <div className="gps__meta mono">
              {manual.accuracy < 0 ? 'accuracy not given' : formatAccuracy(manual.accuracy)} ·{' '}
              {POSITION_SOURCE_LABEL[manual.source as PositionSource]}
            </div>
          </div>
          <span className="state state--synced">Typed</span>
        </div>
      ) : (
      <div className={`gps gps--${phase}`}>
        <div className="gps__icon" aria-hidden="true">
          <IconFix size={26} />
        </div>

        <div className="grow">
          {fix ? (
            <>
              {/* One per line. A coordinate pair on a single wrapping line
                  orphans the hemisphere letter, and these get read out loud
                  over a radio — every character has to stay with its number. */}
              <div className="gps__coords mono">
                <span>{formatCoord(fix.lat, 'lat')}</span>
                <span>{formatCoord(fix.lon, 'lon')}</span>
              </div>
              <div className="gps__meta mono">
                {formatAccuracy(fix.accuracy)}
                {fix.altitude != null && (
                  <> · {Math.round(metersToFeet(fix.altitude))} ft</>
                )}
              </div>
            </>
          ) : (
            <div className="gps__coords mono">{p.coords}</div>
          )}
        </div>

        <span className={`state ${p.state}`}>{p.pill}</span>
      </div>

      )}

      {/* Re-fix is a text action, not a boxed button. A small outlined control
          sitting inside a bigger filled panel is the nesting problem in
          miniature; the 56px target is still here, it just isn't drawn. */}
      <div className="gps__actions">
        <p className="field__hint" style={{ margin: 0 }}>
          {p.hint
            ? p.hint
            : locked
              ? `Within ${LOCK_THRESHOLD_M} m — counts as training grade.`
              : CAPTURE.gpsCaption}
        </p>
        {manual ? (
          <button type="button" className="textbtn" onClick={onClearManual}>
            Use the phone instead
          </button>
        ) : (
          <>
            <button type="button" className="textbtn" onClick={gps.refix}>
              Re-fix
            </button>
            {/* Always available, not only when the phone fails — the handheld is
                often the better instrument even with a good phone fix. */}
            <button type="button" className="textbtn" onClick={onType}>
              Type coordinates
            </button>
          </>
        )}
      </div>
    </div>
  );
}
